import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
} from 'react';

export interface TimeSlot {
  start_time: string;
  end_time: string;
}

export interface BookingCalendarTexts {
  title: string;
  subtitle: string;
  stepDate: string;
  stepTime: string;
  stepReview: string;
  selectDateTitle: string;
  selectDateHint: string;
  selectTimeTitle: string;
  selectTimeHint: string;
  reviewTitle: string;
  reviewHint: string;
  weekdays: string[];
  months: string[];
  prevMonth: string;
  nextMonth: string;
  availableBadge: string;
  noSlotsAvailable: string;
  noSlotsForDate: string;
  loading: string;
  loadError: string;
  retry: string;
  backToDate: string;
  modifyTime: string;
  formTitle: string;
  customerNameLabel: string;
  customerNamePlaceholder: string;
  customerPhoneLabel: string;
  customerPhonePlaceholder: string;
  notesLabel: string;
  notesPlaceholder: string;
  submit: string;
  submitting: string;
  successTitle: string;
  successMessage: string;
  bookAnother: string;
  yourSelection: string;
  dateLabel: string;
  startTimeLabel: string;
  endTimeLabel: string;
  requiredError: string;
  submitError: string;
}

const DEFAULT_TEXTS: BookingCalendarTexts = {
  title: 'Agende sua entrega',
  subtitle: 'Escolha o melhor dia e horário para receber sua entrega.',
  stepDate: 'Data',
  stepTime: 'Horário',
  stepReview: 'Confirmação',
  selectDateTitle: 'Escolha uma data disponível',
  selectDateHint: 'Apenas as datas em destaque estão disponíveis para agendamento.',
  selectTimeTitle: 'Escolha um horário',
  selectTimeHint: 'Selecione o horário que melhor se encaixa na sua rotina.',
  reviewTitle: 'Confirme seus dados',
  reviewHint: 'Revise o horário escolhido e preencha seus dados para finalizar.',
  weekdays: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
  months: [
    'Janeiro',
    'Fevereiro',
    'Março',
    'Abril',
    'Maio',
    'Junho',
    'Julho',
    'Agosto',
    'Setembro',
    'Outubro',
    'Novembro',
    'Dezembro',
  ],
  prevMonth: 'Mês anterior',
  nextMonth: 'Próximo mês',
  availableBadge: 'Disponível',
  noSlotsAvailable: 'Nenhum horário disponível no momento.',
  noSlotsForDate: 'Nenhum horário disponível para esta data.',
  loading: 'Carregando horários disponíveis...',
  loadError: 'Não foi possível carregar os horários. Tente novamente.',
  retry: 'Tentar novamente',
  backToDate: 'Voltar para as datas',
  modifyTime: 'Alterar horário',
  formTitle: 'Seus dados',
  customerNameLabel: 'Nome completo',
  customerNamePlaceholder: 'Seu nome',
  customerPhoneLabel: 'Telefone / WhatsApp',
  customerPhonePlaceholder: '(00) 00000-0000',
  notesLabel: 'Observações (opcional)',
  notesPlaceholder: 'Alguma observação sobre o endereço ou a entrega?',
  submit: 'Confirmar agendamento',
  submitting: 'Confirmando...',
  successTitle: 'Agendamento confirmado!',
  successMessage: 'Recebemos seu horário. Em breve entraremos em contato para confirmar tudo com você.',
  bookAnother: 'Fazer outro agendamento',
  yourSelection: 'Seu horário',
  dateLabel: 'Data',
  startTimeLabel: 'Início',
  endTimeLabel: 'Término',
  requiredError: 'Preencha nome e telefone para continuar.',
  submitError: 'Não foi possível confirmar seu agendamento. Tente novamente.',
};

const DEFAULT_TIMESLOTS_API =
  'https://readdy.ai/api/public/calendar/timeslots/d36a1ed6-70b0-4b48-a6ac-0cd315aa8ee9.fb7cfffe6137847e5b08391ae2247dff526b1fab4f2a57abd46b235ae6db8494';
const DEFAULT_APPOINTMENTS_API =
  'https://readdy.ai/api/public/calendar/appointments/d36a1ed6-70b0-4b48-a6ac-0cd315aa8ee9.fb7cfffe6137847e5b08391ae2247dff526b1fab4f2a57abd46b235ae6db8494';

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function localToday(): string {
  const now = new Date();
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
}

function dateFromStart(startTime: string): string {
  return startTime.split(' ')[0];
}

function timeFromSlot(dateTime: string): string {
  const parts = dateTime.split(' ');
  return parts[1] ? parts[1].slice(0, 5) : dateTime;
}

function closestAvailableDate(dates: string[], today: string): string | null {
  if (dates.length === 0) return null;
  const future = dates.filter((d) => d >= today);
  if (future.length > 0) return future[0];
  return dates[dates.length - 1];
}

export interface BookingCalendarProps {
  timeslotsApi?: string;
  appointmentsApi?: string;
  texts?: Partial<BookingCalendarTexts>;
  containerStyle?: CSSProperties;
}

export default function BookingCalendar(props: BookingCalendarProps) {
  const texts: BookingCalendarTexts = { ...DEFAULT_TEXTS, ...props.texts };
  const timeslotsApi = props.timeslotsApi ?? DEFAULT_TIMESLOTS_API;
  const appointmentsApi = props.appointmentsApi ?? DEFAULT_APPOINTMENTS_API;

  const [slots, setSlots] = useState<TimeSlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [step, setStep] = useState<0 | 1 | 2>(0);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [view, setView] = useState<{ year: number; month: number } | null>(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const didInitRef = useRef(false);

  const slotsByDate = useMemo(() => {
    const map: Record<string, TimeSlot[]> = {};
    for (const s of slots) {
      const d = dateFromStart(s.start_time);
      if (!map[d]) map[d] = [];
      map[d].push(s);
    }
    Object.keys(map).forEach((d) =>
      map[d].sort((a, b) => a.start_time.localeCompare(b.start_time)),
    );
    return map;
  }, [slots]);

  const availableDates = useMemo(
    () => Object.keys(slotsByDate).sort(),
    [slotsByDate],
  );

  const today = localToday();

  const loadSlots = async (isRefresh: boolean) => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await fetch(timeslotsApi);
      if (!res.ok) throw new Error('timeslots_fetch_failed');
      const json = await res.json();
      const raw = Array.isArray(json)
        ? json
        : json?.data ?? json?.slots ?? json?.timeslots ?? [];
      const nextSlots: TimeSlot[] = (raw || [])
        .map((s: { start_time?: unknown; end_time?: unknown }) => ({
          start_time: s.start_time as string,
          end_time: s.end_time as string,
        }))
        .filter(
          (s: TimeSlot) =>
            typeof s.start_time === 'string' && typeof s.end_time === 'string',
        );
      setSlots(nextSlots);

      const dates = Array.from(
        new Set(nextSlots.map((s) => dateFromStart(s.start_time))),
      ).sort();
      const todayStr = localToday();

      if (!isRefresh && !didInitRef.current) {
        didInitRef.current = true;
        const target = closestAvailableDate(dates, todayStr);
        if (target) {
          const [y, m] = target.split('-').map(Number);
          setSelectedDate(target);
          setView({ year: y, month: m - 1 });
        } else {
          setView(null);
        }
      } else if (isRefresh) {
        setView((prev) => {
          const currentPrefix = prev
            ? `${prev.year}-${pad2(prev.month + 1)}`
            : null;
          const hasCurrent =
            currentPrefix && dates.some((d) => d.startsWith(currentPrefix));
          if (!hasCurrent && dates.length > 0) {
            const target = closestAvailableDate(dates, todayStr);
            if (target) {
              const [y, m] = target.split('-').map(Number);
              return { year: y, month: m - 1 };
            }
          }
          return prev;
        });
      }
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSlots(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const calendarCells = useMemo(() => {
    if (!view) return [];
    const firstDay = new Date(view.year, view.month, 1).getDay();
    const daysInMonth = new Date(view.year, view.month + 1, 0).getDate();
    const cells: (string | null)[] = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push(`${view.year}-${pad2(view.month + 1)}-${pad2(d)}`);
    }
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [view]);

  const goToMonth = (delta: number) => {
    if (!view) return;
    const d = new Date(view.year, view.month + delta, 1);
    setView({ year: d.getFullYear(), month: d.getMonth() });
  };

  const pickDate = (date: string) => {
    if (!slotsByDate[date]) return;
    setSelectedDate(date);
    setSelectedSlot(null);
    setStep(1);
  };

  const pickSlot = (slot: TimeSlot) => {
    setSelectedSlot(slot);
    setStep(2);
  };

  const selectedDaySlots = selectedDate ? slotsByDate[selectedDate] ?? [] : [];

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedSlot) return;
    if (!name.trim() || !phone.trim()) {
      setSubmitError(texts.requiredError);
      return;
    }
    setSubmitError(null);
    setSubmitting(true);
    try {
      const payload = {
        customer_name: name.trim(),
        customer_phone: phone.trim(),
        start_time: selectedSlot.start_time,
        end_time: selectedSlot.end_time,
        notes: notes.trim() || undefined,
      };
      const res = await fetch(appointmentsApi, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('appointment_submit_failed');
      setSuccess(true);
      setSelectedDate(null);
      setSelectedSlot(null);
      setName('');
      setPhone('');
      setNotes('');
      setStep(0);
      await loadSlots(true);
    } catch {
      setSubmitError(texts.submitError);
    } finally {
      setSubmitting(false);
    }
  };

  const stepLabels = [texts.stepDate, texts.stepTime, texts.stepReview];

  return (
    <div
      className="bg-white rounded-2xl border border-gray-200 overflow-hidden"
      style={props.containerStyle}
    >
      {/* Cabeçalho */}
      <div className="px-6 py-6 border-b border-gray-100">
        <h3 className="text-lg font-bold text-gray-900">{texts.title}</h3>
        <p className="text-sm text-gray-500 mt-1">{texts.subtitle}</p>
      </div>

      {/* Indicador de etapas */}
      <div className="flex items-center gap-2 px-6 py-4 bg-gray-50 border-b border-gray-100">
        {stepLabels.map((label, i) => {
          const isActive = i === step;
          const isDone = i < step;
          return (
            <div key={i} className="flex items-center gap-2">
              <div
                className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-semibold ${
                  isDone
                    ? 'bg-[#E8610A] text-white'
                    : isActive
                    ? 'bg-[#E8610A] text-white'
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                {isDone ? (
                  <i className="ri-check-line text-xs"></i>
                ) : (
                  <span>{i + 1}</span>
                )}
              </div>
              <span
                className={`text-xs font-semibold ${
                  isActive ? 'text-gray-900' : 'text-gray-400'
                }`}
              >
                {label}
              </span>
              {i < stepLabels.length - 1 && (
                <span className="w-6 h-px bg-gray-200 mx-1" />
              )}
            </div>
          );
        })}
      </div>

      {/* Conteúdo */}
      <div className="p-6">
        {success && (
          <div className="mb-5 rounded-xl bg-green-50 border border-green-200 p-4">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 flex items-center justify-center bg-green-500 text-white rounded-full flex-shrink-0">
                <i className="ri-check-line"></i>
              </div>
              <div>
                <p className="font-semibold text-green-900">{texts.successTitle}</p>
                <p className="text-sm text-green-700 mt-0.5">{texts.successMessage}</p>
              </div>
            </div>
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center gap-2 py-16 text-gray-400 text-sm">
            <i className="ri-loader-4-line animate-spin"></i>
            {texts.loading}
          </div>
        )}

        {!loading && loadError && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-sm mb-4">{texts.loadError}</p>
            <button
              onClick={() => loadSlots(false)}
              className="inline-flex items-center gap-2 bg-[#E8610A] text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-[#C4500A] transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-refresh-line"></i>
              {texts.retry}
            </button>
          </div>
        )}

        {!loading && !loadError && availableDates.length === 0 && (
          <div className="text-center py-12">
            <div className="w-14 h-14 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-4">
              <i className="ri-calendar-2-line text-2xl text-gray-400"></i>
            </div>
            <p className="text-gray-500 text-sm">{texts.noSlotsAvailable}</p>
          </div>
        )}

        {!loading && !loadError && availableDates.length > 0 && (
          <>
            {/* ETAPA 0 — Seleção de data */}
            {step === 0 && view && (
              <div>
                <h4 className="text-sm font-bold text-gray-900 mb-1">
                  {texts.selectDateTitle}
                </h4>
                <p className="text-xs text-gray-500 mb-5">{texts.selectDateHint}</p>

                <div className="flex items-center justify-between mb-4">
                  <button
                    onClick={() => goToMonth(-1)}
                    className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors cursor-pointer"
                    aria-label={texts.prevMonth}
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>
                  <span className="text-sm font-bold text-gray-900">
                    {texts.months[view.month]} {view.year}
                  </span>
                  <button
                    onClick={() => goToMonth(1)}
                    className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors cursor-pointer"
                    aria-label={texts.nextMonth}
                  >
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>

                <div className="grid grid-cols-7 gap-1 mb-1">
                  {texts.weekdays.map((wd) => (
                    <div
                      key={wd}
                      className="text-center text-[11px] font-semibold text-gray-400 uppercase py-1"
                    >
                      {wd}
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-7 gap-1">
                  {calendarCells.map((cell, idx) => {
                    if (cell === null) {
                      return <div key={idx} />;
                    }
                    const isAvailable = Boolean(slotsByDate[cell]);
                    const isSelected = selectedDate === cell;
                    const isToday = cell === today;
                    const dayNum = Number(cell.split('-')[2]);
                    return (
                      <button
                        key={cell}
                        type="button"
                        disabled={!isAvailable}
                        onClick={() => pickDate(cell)}
                        className={`relative h-12 rounded-lg flex flex-col items-center justify-center transition-colors duration-150 ${
                          isSelected
                            ? 'bg-[#E8610A] text-white'
                            : isAvailable
                            ? 'bg-[#E8610A]/10 text-[#C4500A] hover:bg-[#E8610A]/20 cursor-pointer'
                            : 'text-gray-300 cursor-not-allowed'
                        }`}
                      >
                        <span className="text-[10px] leading-none">
                          {texts.weekdays[idx % 7]}
                        </span>
                        <span className="text-sm font-semibold leading-tight">
                          {dayNum}
                        </span>
                        {isAvailable && !isSelected && (
                          <span className="absolute bottom-1 w-1 h-1 rounded-full bg-[#E8610A]" />
                        )}
                        {isToday && !isSelected && (
                          <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-gray-400" />
                        )}
                      </button>
                    );
                  })}
                </div>

                <div className="flex items-center gap-4 mt-4 text-xs text-gray-500">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-[#E8610A]/30" />
                    {texts.availableBadge}
                  </span>
                </div>
              </div>
            )}

            {/* ETAPA 1 — Seleção de horário */}
            {step === 1 && selectedDate && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="text-sm font-bold text-gray-900 mb-1">
                      {texts.selectTimeTitle}
                    </h4>
                    <p className="text-xs text-gray-500">{texts.selectTimeHint}</p>
                  </div>
                  <button
                    onClick={() => setStep(0)}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-arrow-left-line"></i>
                    {texts.backToDate}
                  </button>
                </div>

                <div className="inline-flex items-center gap-2 mb-5 px-3 py-1.5 rounded-full bg-[#E8610A]/10">
                  <i className="ri-calendar-line text-[#C4500A]"></i>
                  <span className="text-sm font-semibold text-[#C4500A]">
                    {selectedDate}
                  </span>
                </div>

                {selectedDaySlots.length === 0 ? (
                  <p className="text-sm text-gray-500 py-6">{texts.noSlotsForDate}</p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {selectedDaySlots.map((slot) => {
                      const isSelected = selectedSlot?.start_time === slot.start_time;
                      return (
                        <button
                          key={slot.start_time}
                          type="button"
                          onClick={() => pickSlot(slot)}
                          className={`px-3 py-3 rounded-lg border text-sm font-semibold transition-colors duration-150 whitespace-nowrap ${
                            isSelected
                              ? 'bg-[#E8610A] border-[#E8610A] text-white'
                              : 'border-gray-200 text-gray-700 hover:border-[#E8610A] hover:text-[#C4500A] cursor-pointer'
                          }`}
                        >
                          {timeFromSlot(slot.start_time)} – {timeFromSlot(slot.end_time)}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* ETAPA 2 — Revisão + formulário */}
            {step === 2 && selectedSlot && selectedDate && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="text-sm font-bold text-gray-900 mb-1">
                      {texts.reviewTitle}
                    </h4>
                    <p className="text-xs text-gray-500">{texts.reviewHint}</p>
                  </div>
                  <button
                    onClick={() => setStep(1)}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-[#C4500A] hover:text-[#A84000] transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-time-line"></i>
                    {texts.modifyTime}
                  </button>
                </div>

                {/* Revisão do horário (somente leitura) */}
                <div className="rounded-xl bg-gray-50 border border-gray-200 p-4 mb-6">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                    {texts.yourSelection}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-gray-400 mb-0.5">{texts.dateLabel}</p>
                      <p className="text-sm font-semibold text-gray-900">
                        {selectedDate}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 mb-0.5">{texts.startTimeLabel}</p>
                      <p className="text-sm font-semibold text-gray-900">
                        {timeFromSlot(selectedSlot.start_time)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 mb-0.5">{texts.endTimeLabel}</p>
                      <p className="text-sm font-semibold text-gray-900">
                        {timeFromSlot(selectedSlot.end_time)}
                      </p>
                    </div>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                      {texts.customerNameLabel}
                    </label>
                    <input
                      type="text"
                      name="customer_name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder={texts.customerNamePlaceholder}
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A] focus:ring-2 focus:ring-[#E8610A]/20 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                      {texts.customerPhoneLabel}
                    </label>
                    <input
                      type="tel"
                      name="customer_phone"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder={texts.customerPhonePlaceholder}
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A] focus:ring-2 focus:ring-[#E8610A]/20 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                      {texts.notesLabel}
                    </label>
                    <textarea
                      name="notes"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder={texts.notesPlaceholder}
                      maxLength={500}
                      rows={3}
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A] focus:ring-2 focus:ring-[#E8610A]/20 transition-all resize-none"
                    />
                  </div>

                  {submitError && (
                    <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                      {submitError}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#E8610A] text-white text-sm font-semibold px-6 py-3.5 rounded-xl hover:bg-[#C4500A] transition-colors duration-200 whitespace-nowrap cursor-pointer disabled:opacity-60"
                  >
                    {submitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        {texts.submitting}
                      </>
                    ) : (
                      <>
                        <i className="ri-calendar-check-line"></i>
                        {texts.submit}
                      </>
                    )}
                  </button>
                </form>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}